import { useEffect, useRef, useState } from "react";
import Connect from "@mono.co/connect.js";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Bank, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SampleBadge } from "@/components/MonoIdentityVerify";

/** Opens Mono Connect (or a labelled sample linker), exchanges the code server-side, calls onLinked(account). */
export default function MonoConnectButton({ onLinked, label = "Link your bank account", testid = "mono-connect-button", disabled }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const widget = useRef(null);

  const exchange = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mono/connect/exchange", { code });
      toast.success(`${data.bank_name || "Bank"} ${data.account_number_masked} linked`);
      await onLinked(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/mono/config").then(({ data }) => setCfg(data)).catch(() => setCfg({ live: false }));
  }, []);

  useEffect(() => {
    if (!cfg?.live || !cfg.public_key) return undefined;
    const c = new Connect({ key: cfg.public_key, scope: "auth", onSuccess: ({ code }) => exchange(code) });
    c.setup();
    widget.current = c;
    return () => { widget.current = null; };
  }, [cfg]); // eslint-disable-line

  const open = () => (cfg?.live ? widget.current?.open() : setSampleOpen(true));

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={open} disabled={disabled || busy || !cfg} className="btn-primary disabled:opacity-50" data-testid={testid}>
          {busy ? <CircleNotch className="animate-spin" size={16} /> : <><Bank size={16} weight="fill" /> {label}</>}
        </button>
        {cfg && !cfg.live && <SampleBadge testid={`${testid}-sample-badge`} />}
      </div>
      <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
        <DialogContent data-testid="mono-sample-dialog">
          <DialogHeader>
            <DialogTitle>Sample bank link</DialogTitle>
            <DialogDescription>
              Mono isn't connected yet, so this links a sample account with made-up transactions. Nothing is read from a real bank.
            </DialogDescription>
          </DialogHeader>
          <button type="button" className="btn-primary justify-center w-full" data-testid="mono-sample-continue"
            onClick={() => { setSampleOpen(false); exchange(`sample_${Date.now()}`); }}>
            <CheckCircle size={16} weight="fill" /> Link sample account
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
